import { type QueryType } from '@chronosphereio/ai';
import {
  Analytics,
  TimeFormat,
  /**
   * Hook to access the feature flags from the VizFrameworkProvider.
   * This is the only place where feature flags should be accessed.
   */
  useFeatureFlags,
} from '@chronosphereio/core';
import { HTTPPrometheusClient } from '@chronosphereio/editors/prometheus';
import constate from 'constate';
import { useMemo } from 'react';

export interface QueryBuilderCommentsProps {
  entityId: string;
  entityType: string;
}

export interface QueryBuilderMetricDescriptionProps {
  metricName: string;
}

/** Params passed to the hint render function; focus and emptiness are derived from the field locally (no debounce). */
export interface FieldSuggestionHintParams {
  focused: boolean;
  isEmpty: boolean;
  /** Whether the field is currently displaying auto-filled text (black overlay, transparent input text). */
  isAutoFilled: boolean;
}

/** Props for field-specific suggestion UI elements. Overlay vs hint and focus are decided inside the field (using real-time value). */
export interface FieldSuggestionProps {
  /** Ghost text overlay to render when the field is empty (gray text, visibility determined by the field). */
  overlay: React.ReactNode;
  /** Auto-fill overlay to render when the field value matches the suggestion (black text, shown when not focused). */
  autoFillOverlay?: React.ReactNode;
  /** Raw suggestion text. Used by the field to sync auto-fill value and detect matches on blur. */
  suggestionText?: string | null;
  /** Key down handler; receives current field value so Enter-to-accept can use real-time emptiness */
  onKeyDown: (e: React.KeyboardEvent, value: string) => void;
  /** Hint content; called with current focus, emptiness, and auto-fill state so the field can show the right message. */
  hint: (params: FieldSuggestionHintParams) => React.ReactNode;
}

/** Props for button-based field generation UI (Generate / Regenerate / Undo). */
export interface FieldGenerateButtonProps {
  /** Trigger AI generation (or re-generation) for this field. */
  onGenerate: () => void;
  /** Whether a generation request is currently in-flight. */
  isGenerating: boolean;
  /** Whether a value has been generated at least once (controls Generate vs Regenerate label). */
  hasGenerated: boolean;
  /** When defined, an undo action is available to revert to the previous value. */
  onUndo?: () => void;
}

/** Discriminated union for per-field AI props. The mode determines which field component to render. */
export type FieldAIProps =
  | { mode: 'suggestion'; suggestion: FieldSuggestionProps }
  | { mode: 'generate'; generate: FieldGenerateButtonProps };

export interface PanelNameDescriptionGeneratorProps {
  /** Array of query strings from the panel */
  queries: string[];
  /** Type of each query (must match length of queries array) */
  queryTypes: QueryType[];
  /** Panel visualization type (e.g., "TimeSeriesChart", "StatChart") */
  panelType: string;
  /** Current panel name (used to evaluate if suggestion is needed) */
  currentName: string;
  /** Current panel description (used to evaluate if suggestion is needed) */
  currentDescription: string;
  /** Whether this is a new panel (Add mode) vs editing an existing panel. Only used by the suggestion flow. */
  isNewPanel?: boolean;
  /** Callback to apply the generated name and description */
  onGenerate: (name: string, description: string) => void;
  /** Render function that receives AI props for each field */
  children: (props: { name: FieldAIProps; description: FieldAIProps }) => React.ReactNode;
}

export type PrometheusQueryEditorToolbarProps = {
  query?: string;
  onUpdateQuery: (query: string, options?: { shouldRun: boolean; shouldTriggerOnChange?: boolean }) => void;
  mode: 'code' | 'builder';
};

export type VizFrameworkConfigArgs = {
  /**
   * Filters which plugins are shown in the PluginKindSelect.
   * If not provided, all plugins are shown.
   * @param kind The kind of plugin to filter.
   */
  filterPlugins?: (kind: string) => boolean;

  /**
   * Controls if schema validation is enabled.
   * @default false
   */
  enableSchemaValidation?: boolean;

  /**
   * The scrape interval for the Prometheus datasource.
   */
  prometheusScrapeIntervalSeconds?: number;

  /**
   * Controls the limit of datapoints that can be fetched from a datasource.
   * This value is passed to the M3 backend to limit the number of datapoints that can be fetched for a single query.
   */
  datapointsLimit?: number;

  /**
   * Controls the limit of series that can be fetched from a datasource.
   * This value is passed to the M3 backend to limit the number of datapoints that can be fetched for a single query.
   */
  seriesLimit?: number;

  /**
   * Controls if query truncation is enabled. On by default to guard against heavy queries locking up the browser.
   * When disabled, backend query limit headers are not set.
   * @default true
   */
  enableQueryTruncation?: boolean;

  /**
   * The URL to the Prometheus instance. This generally does not need to be set as the default is `/data/m3`.
   */
  prometheusURL?: string;

  /**
   * Injectable component for the Prometheus query editor toolbar.
   */
  PrometheusQueryEditorToolbar?: React.ComponentType<PrometheusQueryEditorToolbarProps>;

  /**
   * Common headers to be sent with all datasource requests.
   * We use this to send the public dashboard id to the datasource.
   */
  datasourceRequestHeaders?: Record<string, string>;

  /**
   * When true, "safe mode" will be enabled for all visualizations. This will warn the user before rendering
   * a visualization if it includes a very large number of series that may cause performance issues.
   * @default false
   */
  enableSafeMode?: boolean;

  /**
   * Optional analytics client for tracking events.
   */
  analyticsClient?: Analytics;

  /**
   * Optional Prometheus client for querying Prometheus database.
   */
  promClient?: HTTPPrometheusClient;

  /**
   * When true, mock plugins are enabled for the purpose of providing examples in code, demoing their
   * behavior in cloud, and testing. This flag should only be enabled in internal-facing tenants.
   */
  enableVizFrameworkMocks?: boolean;

  /**
   * When true, the user has the ability to modify their account time zone. This impacts time zone
   * related information shown within the framework (e.g. time picker).
   */
  timezonePreferencesEnabled?: boolean;

  /**
   * Max number of datapoints an individual series can have, impacts the step calculation used in queries.
   */
  maxDatapointsPerSeries?: number;

  /**
   * Whether to enable the experimental Prometheus downsampling feature.
   */
  enableExperimentalPrometheusDownsampling?: boolean;

  /**
   * Whether to enable variable opportunity suggestions (cross-query and label-name-match)
   * in the panel editor query inputs.
   */
  enableVariableOpportunities?: boolean;

  /**
   * User's preference for 12 or 24 hour time.
   */
  timeFormat?: TimeFormat;

  /**
   * Injectable comments component used by the PromQL query builder:
   */
  QueryBuilderComments?: React.ComponentType<QueryBuilderCommentsProps>;

  /**
   * Injectable metric description component used by the PromQL query builder.
   */
  QueryBuilderMetricDescription?: React.ComponentType<QueryBuilderMetricDescriptionProps>;

  /**
   * When true, enables autocompletion of built-in variables like $__rate_interval
   * in duration contexts (e.g., inside square brackets like `[5m]` or `[$__rate_interval]`).
   * This should be enabled for dashboards and metrics explorer where these variables are supported.
   */
  enableBuiltInVariables?: boolean;

  /**
   * Injectable component for AI-powered panel name and description suggestions.
   * When provided, automatically generates suggestions when queries change and displays
   * an approval UI for accepting or rejecting the suggestions.
   */
  PanelNameDescriptionGenerator?: React.ComponentType<PanelNameDescriptionGeneratorProps>;
};

function useConfigInternal(args: VizFrameworkConfigArgs) {
  const featureFlags = useFeatureFlags();
  return useMemo(() => {
    return {
      featureFlags,
      ...args,
    };
  }, [args, featureFlags]);
}

export const [VizFrameworkConfigProvider, useConfigStrict] = constate(useConfigInternal);

/**
 * Hook to access the config object from the VizFrameworkProvider.
 */
export function useVizFrameworkConfig() {
  try {
    return useConfigStrict();
  } catch (_e) {
    return undefined;
  }
}
